/**
 * The assistant in the tenant shell: the rail button and the chat, which stays open and keeps its
 * state while the person moves around the app (it's what lets it open pages for them).
 */
import { Bot } from "lucide-react";
import { createContext, lazy, Suspense, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";

import { IconButton } from "@/components/ui";

type AgentControls = {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  trigger: React.RefObject<HTMLButtonElement | null>;
};

const AgentContext = createContext<AgentControls | null>(null);

// The chat loads when first opened: most page views never need it.
const AgentDialog = lazy(() => import("./AgentDialog").then((m) => ({ default: m.AgentDialog })));

export function AgentProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [everOpened, setEverOpened] = useState(false);
  const trigger = useRef<HTMLButtonElement | null>(null);
  const change = useCallback((next: boolean) => {
    setOpen(next);
    if (next) setEverOpened(true);
  }, []);
  const toggle = useCallback(() => {
    setOpen((current) => !current);
    setEverOpened(true);
  }, []);
  const value = useMemo(() => ({ open, setOpen: change, toggle, trigger }), [open, change, toggle]);
  return (
    <AgentContext.Provider value={value}>
      {children}
      {everOpened && (
        <Suspense fallback={null}>
          <AgentDialog />
        </Suspense>
      )}
    </AgentContext.Provider>
  );
}

export function useAgent(): AgentControls {
  const value = useContext(AgentContext);
  if (!value) throw new Error("useAgent must be used inside AgentProvider");
  return value;
}

/** Shown to everyone: a company without the assistant opens it to see what it does and ask for it. */
export function AgentButton({ size = "rail" }: { size?: "rail" | "bar" }) {
  const { open, toggle, trigger } = useAgent();
  return (
    <span data-tour="agent-button" className="inline-flex">
      <IconButton
        ref={trigger}
        label="Assistant"
        tooltipSide={size === "rail" ? "right" : undefined}
        tooltip={size === "rail"}
        aria-expanded={open}
        aria-haspopup="dialog"
        icon={<Bot className={size === "rail" ? "size-[18px]" : "size-5"} />}
        onClick={toggle}
      />
    </span>
  );
}
